import type { ReactElement } from 'react';
import { Info } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ConfidenceBadge,
  ConfidenceScaleExplanation,
} from '@/components/grouped-alerts/SecretConfidenceBadge';
import type { SecretConfidence } from '@kirin/api-client/helpers/secret-confidence';

/**
 * An "i" info icon that reveals the shared {@link ConfidenceScaleExplanation}
 * (the same body the alert confidence badge shows) on hover/focus. Rendered
 * next to the setting's card title so the confidence scale is discoverable
 * without having to hover a badge. Exported so the org and personal Policy
 * Settings cards use an identical affordance.
 */
export function SecretConfidenceInfoTooltip(): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="What do the confidence levels mean?"
          className="inline-flex text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-full"
        >
          <Info className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <ConfidenceScaleExplanation />
      </TooltipContent>
    </Tooltip>
  );
}

SecretConfidenceInfoTooltip.displayName = 'SecretConfidenceInfoTooltip';

// High first (most restrictive → hides the most) so the ordering reads like the
// enforcement-severity control (strongest at the top). `high` is the BE default.
const CONFIDENCE_OPTIONS: readonly SecretConfidence[] = [
  'high',
  'medium',
  'low',
] as const;

interface SecretConfidenceThresholdControlProps {
  value: SecretConfidence;
  onChange: (confidence: SecretConfidence) => void;
  disabled?: boolean;
  saving?: boolean;
  canEdit?: boolean;
}

export function SecretConfidenceThresholdControl({
  value,
  onChange,
  disabled = false,
  saving = false,
  canEdit = true,
}: SecretConfidenceThresholdControlProps): ReactElement {
  return (
    <div>
      <Select
        value={value}
        onValueChange={v => onChange(v as SecretConfidence)}
        disabled={disabled || saving || !canEdit}
      >
        <SelectTrigger className="w-fit min-w-44">
          <SelectValue placeholder="Select confidence" />
        </SelectTrigger>
        <SelectContent>
          {CONFIDENCE_OPTIONS.map(confidence => (
            <SelectItem key={confidence} value={confidence}>
              <ConfidenceBadge
                confidence={confidence}
                className="bg-transparent border-0 p-0"
              />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

SecretConfidenceThresholdControl.displayName =
  'SecretConfidenceThresholdControl';
